/**
 * Generic client-side table sort hook
 * Supports ascending/descending toggle per column key
 */

import { useMemo, useState, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

type KeyExtractors<T> = Record<string, (item: T) => unknown>;

interface UseTableSortResult<T> {
  sorted: T[];
  sortState: SortState | null;
  toggleSort: (key: string) => void;
}

export function useTableSort<T>(
  items: T[],
  defaultSort?: SortState,
  keyExtractors?: KeyExtractors<T>
): UseTableSortResult<T> {
  const [sortState, setSortState] = useState<SortState | null>(
    defaultSort ?? null
  );

  const toggleSort = useCallback((key: string) => {
    setSortState((prev) => {
      if (prev?.key === key) {
        return prev.direction === 'asc'
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sortState) return items;
    const { key, direction } = sortState;
    const extractor = keyExtractors?.[key];
    return [...items].sort((a, b) => {
      const aVal = extractor ? extractor(a) : (a as Record<string, unknown>)[key];
      const bVal = extractor ? extractor(b) : (b as Record<string, unknown>)[key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [items, sortState, keyExtractors]);

  return { sorted, sortState, toggleSort };
}
